import { useCallback, useEffect, useState } from "react";
import { ArrowLeftRight, Check, X, AlertTriangle, Inbox } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { fmtShift, fmtWhen, STATUS_LABEL, STATUS_TONE } from "./shiftFormat";

const StatusChip = ({ status, testid }) => (
  <span data-testid={testid} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap ${STATUS_TONE[status] || ""}`}>
    {STATUS_LABEL[status] || status}
  </span>
);

const Summary = ({ r }) => (
  <div className="min-w-0">
    <div className="text-sm font-semibold">
      {r.type === "drop" ? `${r.requester_name} wants to drop` : `${r.requester_name} → ${r.target_name}`}
    </div>
    <div className="text-[12px] text-zinc-500">{fmtShift(r.shift)}</div>
    {r.trade_shift && <div className="text-[12px] text-zinc-500">In return: {fmtShift(r.trade_shift)}</div>}
    {r.reason && <div className="text-[11px] text-zinc-400 mt-0.5">“{r.reason}”</div>}
    {r.decision_note && <div className="text-[11px] text-zinc-400 mt-0.5">Note: {r.decision_note}</div>}
    {r.conflict && (
      <div className="text-[11px] text-rose-600 dark:text-rose-300 mt-0.5 flex items-center gap-1" data-testid={`shift-req-conflict-${r.id}`}>
        <AlertTriangle className="h-3 w-3"/>{r.target_name} already has a shift at that time
      </div>
    )}
  </div>
);

const Row = ({ r, children, testid }) => (
  <li data-testid={testid} className="flex items-start justify-between gap-3 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3 py-2.5">
    <Summary r={r}/>
    <div className="flex items-center gap-1.5 shrink-0">{children}</div>
  </li>
);

const Section = ({ title, items, render, testid }) => (items?.length ? (
  <div data-testid={testid}>
    <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">{title} ({items.length})</div>
    <ul className="space-y-2">{items.map(render)}</ul>
  </div>
) : null);

// v2.8+E345 · swap / drop requests — incoming, my own, and (managers) approvals.
export const ShiftRequestsPanel = ({ refreshKey, onChanged }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi.get("/admin/shift-requests");
      setData(d);
    } catch (_e) { setData(null); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (id, action, msg) => {
    try {
      await adminApi.post(`/admin/shift-requests/${id}/${action}`, {});
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update request");
    }
  };

  if (!data) return null;
  const { incoming = [], mine = [], pending_approval = [], waiting_on_coworker = [], recent = [], is_manager } = data;
  const empty = !incoming.length && !mine.length && !pending_approval.length && !waiting_on_coworker.length && !recent.length;

  return (
    <Card className="p-4 space-y-4" data-testid="shift-requests-panel">
      <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 flex items-center gap-1.5">
        <ArrowLeftRight className="h-4 w-4"/>Swap &amp; drop requests
      </div>
      {empty && (
        <div className="text-sm text-zinc-400 py-3 text-center flex items-center justify-center gap-2" data-testid="shift-requests-empty">
          <Inbox className="h-4 w-4"/>No requests right now.
        </div>
      )}
      <Section title="Needs your approval" testid="shift-requests-pending-approval" items={pending_approval} render={(r) => (
        <Row key={r.id} r={r} testid={`shift-req-approval-${r.id}`}>
          {r.self_involved ? (
            <span className="text-[11px] text-zinc-500 max-w-[160px] text-right" data-testid={`shift-req-self-${r.id}`}>You're part of this — another supervisor must approve</span>
          ) : (
            <Button size="sm" onClick={() => act(r.id, "approve", "Request approved — schedule updated")} data-testid={`shift-req-approve-${r.id}`} className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"><Check className="h-3.5 w-3.5 mr-1"/>Approve</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => act(r.id, "reject", "Request rejected")} data-testid={`shift-req-reject-${r.id}`} className="h-8 text-xs"><X className="h-3.5 w-3.5 mr-1"/>Reject</Button>
        </Row>
      )}/>
      <Section title="Asked of you" testid="shift-requests-incoming" items={incoming} render={(r) => (
        <Row key={r.id} r={r} testid={`shift-req-incoming-${r.id}`}>
          <Button size="sm" onClick={() => act(r.id, "accept", "Accepted — sent to a supervisor for approval")} data-testid={`shift-req-accept-${r.id}`} className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"><Check className="h-3.5 w-3.5 mr-1"/>Accept</Button>
          <Button size="sm" variant="outline" onClick={() => act(r.id, "decline", "Declined")} data-testid={`shift-req-decline-${r.id}`} className="h-8 text-xs"><X className="h-3.5 w-3.5 mr-1"/>Decline</Button>
        </Row>
      )}/>
      {is_manager && (
        <Section title="Waiting on coworker" testid="shift-requests-waiting" items={waiting_on_coworker} render={(r) => (
          <Row key={r.id} r={r} testid={`shift-req-waiting-${r.id}`}>
            <Button size="sm" variant="ghost" onClick={() => act(r.id, "reject", "Request rejected")} data-testid={`shift-req-reject-${r.id}`} className="h-8 text-xs text-zinc-500 hover:text-rose-500">Reject</Button>
          </Row>
        )}/>
      )}
      <Section title="Your requests" testid="shift-requests-mine" items={mine} render={(r) => (
        <Row key={r.id} r={r} testid={`shift-req-mine-${r.id}`}>
          <StatusChip status={r.status} testid={`shift-req-status-${r.id}`}/>
          {(r.status === "pending_coworker" || r.status === "pending_supervisor") && (
            <Button size="sm" variant="ghost" onClick={() => act(r.id, "cancel", "Request cancelled")} data-testid={`shift-req-cancel-${r.id}`} className="h-8 text-xs text-zinc-500 hover:text-rose-500">Cancel</Button>
          )}
        </Row>
      )}/>
      {is_manager && (
        <Section title="Recently decided" testid="shift-requests-recent" items={recent} render={(r) => (
          <Row key={r.id} r={r} testid={`shift-req-recent-${r.id}`}>
            <div className="text-right">
              <StatusChip status={r.status} testid={`shift-req-recent-status-${r.id}`}/>
              {r.decided_by && <div className="text-[10px] text-zinc-400 mt-1">{r.decided_by} · {fmtWhen(r.decided_at, { weekday: undefined })}</div>}
            </div>
          </Row>
        )}/>
      )}
    </Card>
  );
};

export default ShiftRequestsPanel;
